<script setup lang="ts">
/**
 * ModalRoot — registry + lifecycle controller для модалок (inbox-134
 * extends inbox-104).
 *
 * Делает:
 *   - Teleport в body (поверх любого layout'a)
 *   - ESC closes (если !modals.pending)
 *   - body-scroll-lock через modal store (open/close уже выставляет
 *     document.body.style.overflow)
 *   - Focus trap внутри активной модалки (Tab / Shift+Tab cycle)
 *   - Auto-focus первого focusable элемента при open
 *
 * Registry per-modal-id — конкретные модалки рендерятся по activeKey.
 * Каждая использует UiModal как template wrapper (overlay/card/styling).
 *
 * Usage: <ModalRoot /> в App.vue / CabinetLayout. Открытие через
 *   useModalStore().open('create-link', { ... }).
 */

import { onMounted, onBeforeUnmount, watch, nextTick, ref } from 'vue';
import { useModalStore } from '@/stores/modal';
import CreateLinkModal from './CreateLinkModal.vue';
import EditLinkModal from './EditLinkModal.vue';
import PreviewLinkModal from './PreviewLinkModal.vue';
import CreateBioModal from './CreateBioModal.vue';
import ConnectIntegrationModal from './ConnectIntegrationModal.vue';
import ImportCsvModal from './ImportCsvModal.vue';
import ExportModal from './ExportModal.vue';
import AbTestModal from './AbTestModal.vue';
import QrCustomizerModal from './QrCustomizerModal.vue';
import RoutingBuilderModal from './RoutingBuilderModal.vue';
import ConfirmDeleteModal from './ConfirmDeleteModal.vue';
import UpgradePlanModal from './UpgradePlanModal.vue';
import CreateCampaignModal from './CreateCampaignModal.vue';
import AddApiKeyModal from './AddApiKeyModal.vue';
import CancelSubscriptionModal from './CancelSubscriptionModal.vue';
import ChangeRoleModal from './ChangeRoleModal.vue';
import BulkOpsModal from './BulkOpsModal.vue';
import OnboardingCoachModal from './OnboardingCoachModal.vue';
import BlockFraudModal from './BlockFraudModal.vue';

const modals = useModalStore();
const rootEl = ref<HTMLElement | null>(null);

function onKey(e: KeyboardEvent) {
  if (!modals.isOpen) return;
  if (e.key === 'Escape' && !modals.pending) {
    e.preventDefault();
    modals.close();
    return;
  }
  if (e.key === 'Tab') {
    trapFocus(e);
  }
}

/** Focus trap — циклит Tab / Shift+Tab внутри открытой модалки. */
function trapFocus(e: KeyboardEvent) {
  const root = rootEl.value;
  if (!root) return;
  const focusables = root.querySelectorAll<HTMLElement>(
    'a[href]:not([disabled]), button:not([disabled]), input:not([disabled]),' +
      ' select:not([disabled]), textarea:not([disabled]),' +
      ' [tabindex]:not([tabindex="-1"]):not([disabled])',
  );
  if (focusables.length === 0) return;
  const first = focusables[0]!;
  const last = focusables[focusables.length - 1]!;
  const active = document.activeElement as HTMLElement | null;
  if (e.shiftKey) {
    if (active === first || !root.contains(active)) {
      e.preventDefault();
      last.focus();
    }
  } else if (active === last) {
    e.preventDefault();
    first.focus();
  }
}

/** При открытии — focus первый focusable элемент. */
async function autoFocus() {
  await nextTick();
  const root = rootEl.value;
  if (!root) return;
  const target = root.querySelector<HTMLElement>(
    '[autofocus], input:not([disabled]):not([type=hidden]), select:not([disabled]),' +
      ' textarea:not([disabled]), button:not([disabled])',
  );
  target?.focus();
}

watch(
  () => modals.isOpen,
  (open) => {
    if (open) void autoFocus();
  },
);

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', onKey);
  }
});

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('keydown', onKey);
  }
});
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="modals.isOpen" ref="rootEl">
        <!-- Modal registry per-modal-id — switch по modals.activeKey. -->
        <CreateLinkModal v-if="modals.activeKey === 'create-link'" />
        <EditLinkModal v-else-if="modals.activeKey === 'edit-link'" />
        <PreviewLinkModal v-else-if="modals.activeKey === 'preview-link'" />
        <BulkOpsModal v-else-if="modals.activeKey === 'bulk-ops'" />
        <ChangeRoleModal v-else-if="modals.activeKey === 'change-role'" />
        <OnboardingCoachModal v-else-if="modals.activeKey === 'onboarding-coach'" />
        <BlockFraudModal v-else-if="modals.activeKey === 'block-fraud'" />
        <ImportCsvModal v-else-if="modals.activeKey === 'import-csv'" />
        <ExportModal v-else-if="modals.activeKey === 'export'" />
        <AbTestModal v-else-if="modals.activeKey === 'ab-test'" />
        <QrCustomizerModal v-else-if="modals.activeKey === 'qr-customizer'" />
        <RoutingBuilderModal v-else-if="modals.activeKey === 'routing-builder'" />
        <ConfirmDeleteModal v-else-if="modals.activeKey === 'confirm-delete'" />
        <UpgradePlanModal v-else-if="modals.activeKey === 'upgrade-plan'" />
        <CreateCampaignModal v-else-if="modals.activeKey === 'create-campaign'" />
        <AddApiKeyModal v-else-if="modals.activeKey === 'add-api-key'" />
        <CancelSubscriptionModal v-else-if="modals.activeKey === 'cancel-subscription'" />
        <!-- inbox-359 — CreateBioModal был импортирован в inbox-179 но
             register-в-ModalRoot коммит не залит. Без этой строки клик
             «Создать» на /bio открывал ничего. -->
        <CreateBioModal v-else-if="modals.activeKey === 'create-bio'" />
        <ConnectIntegrationModal v-else-if="modals.activeKey === 'connect-integration'" />
        <!-- TODO: <AddDomainModal v-else-if="modals.activeKey === 'add-domain'" /> -->
        <!-- TODO: <AddWebhookModal />, <InviteTeammateModal /> -->
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-active :deep(.modal-card),
.modal-leave-active :deep(.modal-card) {
  transition: transform 0.2s, opacity 0.2s;
}
.modal-enter-from :deep(.modal-card),
.modal-leave-to :deep(.modal-card) {
  opacity: 0;
  transform: translateY(8px) scale(0.97);
}
</style>
